/**
 * Persistent first-party device id. Unlike sessionId (per-page-load), this is
 * stable across visits so the worker can flag returning + cross-link devices.
 *
 * Stored in BOTH localStorage and a long-lived cookie — each is a fallback for
 * the other (a user who clears one often keeps the other). No module-level cache:
 * localStorage is the source of truth and reads are cheap, which also keeps the
 * function trivially testable.
 */
export const DEVICE_ID_KEY = '__tc_did';

const TWO_YEARS_SEC = 60 * 60 * 24 * 730;

function makeId(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${TWO_YEARS_SEC}; path=/; SameSite=Lax`;
}

export function getDeviceId(): string {
  if (typeof window === 'undefined') return 'ssr';

  let id: string | null = null;
  try {
    id = localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    // localStorage blocked (private mode / disabled) — fall through to cookie
  }
  if (!id) id = readCookie(DEVICE_ID_KEY);
  if (!id || id.length < 8) id = makeId();

  // Re-sync both stores so a cleared one is repopulated from the surviving one.
  try {
    localStorage.setItem(DEVICE_ID_KEY, id);
  } catch {
    // ignore
  }
  try {
    writeCookie(DEVICE_ID_KEY, id);
  } catch {
    // ignore
  }
  return id;
}
